import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, AlertCircle, Check, ArrowRight, Copy, Loader2 } from 'lucide-react';
import QRCode from 'qrcode';
import { adminApi, useAdminAuth } from './AdminAuthContext';

/**
 * Setup wizard — 4 steps:
 *   0) verify the URL token
 *   1) pick a password (≥10 chars)
 *   2) scan the QR with an authenticator app + confirm the 6-digit code
 *   3) save the recovery codes — one-time display
 */
const PWD_MIN = 10;

function passwordStrength(pwd) {
  let score = 0;
  if (pwd.length >= PWD_MIN) score++;
  if (pwd.length >= 14) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return Math.min(score, 4);
}

const STRENGTH_COLORS = ['#C03A1A', '#E07A2A', '#E0A930', '#9CCB3B', '#7FC242'];
const STRENGTH_LABEL = ['zu schwach', 'schwach', 'okay', 'stark', 'exzellent'];

export default function AdminSetupPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAdminAuth();
  const token = params.get('token') || '';

  const [stage, setStage] = useState('verifying'); // verifying | invalid | password | totp | done
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) { setStage('invalid'); setError('Setup-Link fehlt oder ist beschädigt.'); return; }
    (async () => {
      try {
        const { data } = await adminApi.post('/admin/setup/verify-token', { token });
        setEmail(data.email);
        setStage('password');
      } catch (e) {
        setStage('invalid');
        setError(e?.response?.data?.detail || 'Setup-Link ungültig oder abgelaufen.');
      }
    })();
  }, [token]);

  const beginTotp = async () => {
    setError(null);
    if (password.length < PWD_MIN) { setError(`Mindestens ${PWD_MIN} Zeichen.`); return; }
    if (password !== password2) { setError('Passwörter stimmen nicht überein.'); return; }
    if (passwordStrength(password) < 2) { setError('Passwort ist zu schwach.'); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post('/admin/setup/begin-2fa', { token });
      if (data.otpauth_uri) {
        const qrDataUrl = await QRCode.toDataURL(data.otpauth_uri, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 220,
          color: { dark: '#1F1D1A', light: '#FFFFFF' },
        });
        setQr(qrDataUrl);
      } else {
        setQr(data.qr_data_url);
      }
      setSecret(data.secret);
      setStage('totp');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fehler beim Generieren des QR-Codes.');
    } finally { setBusy(false); }
  };

  const completeSetup = async () => {
    setError(null);
    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) { setError('Bitte 6-stelligen Code eingeben.'); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post('/admin/setup/complete', { token, password, totp_code: otp });
      setRecoveryCodes(data.recovery_codes || []);
      setStage('done');
      // Backend already set the auth cookies — refresh the context user.
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Setup fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[admin-setup] clipboard write failed:', err);
      }
    }
  };

  const goToAdmin = () => navigate('/admin/leads', { replace: true });

  const strength = passwordStrength(password);

  return (
    <div className="min-h-screen bg-[#FAF8F2] flex items-center justify-center px-4 py-10" data-testid="admin-setup-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E5E0D2] p-7 lg:p-9 shadow-[0_20px_50px_-25px_rgba(31,29,26,0.3)]">
        <div className="flex items-center gap-3 mb-6">
          <span className="inline-flex w-10 h-10 rounded-full bg-[#F26B1A]/10 text-[#F26B1A] items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div>
            <p className="font-display text-[20px] leading-none">Admin einrichten</p>
            <p className="text-[12px] text-[#5C5852] mt-1">horrenmaker.nl</p>
          </div>
        </div>

        {stage === 'verifying' && (
          <p data-testid="admin-setup-verifying" className="text-[14px] text-[#5C5852]">Setup-Link wird geprüft …</p>
        )}

        {stage === 'invalid' && (
          <div data-testid="admin-setup-invalid">
            <p className="inline-flex items-start gap-2 text-[14px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>
            <Link to="/admin/login" className="mt-4 inline-block text-[13px] underline">Zur Anmeldung</Link>
          </div>
        )}

        {stage === 'password' && (
          <div data-testid="admin-setup-password">
            <p className="text-[14px] text-[#5C5852] mb-5">Lege ein Passwort fest für <strong className="text-[#1F1D1A]">{email}</strong>.</p>
            <label className="block mb-4">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Passwort</span>
              <input type="password" data-testid="admin-setup-pwd"
                value={password} onChange={(e) => setPassword(e.target.value)}
                autoFocus placeholder="min. 10 Zeichen"
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[15px]"
              />
              {password.length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-[#E5E0D2] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${(strength / 4) * 100}%`, backgroundColor: STRENGTH_COLORS[strength] }} />
                  </div>
                  <span className="text-[11px] tabular-nums" style={{ color: STRENGTH_COLORS[strength] }}>{STRENGTH_LABEL[strength]}</span>
                </div>
              )}
            </label>
            <label className="block mb-5">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Passwort wiederholen</span>
              <input type="password" data-testid="admin-setup-pwd2"
                value={password2} onChange={(e) => setPassword2(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[15px]"
              />
            </label>
            {error && <p data-testid="admin-setup-err" className="mb-3 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}
            <button type="button" onClick={beginTotp} disabled={busy} data-testid="admin-setup-next"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-50 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Weiter zu 2FA
            </button>
          </div>
        )}

        {stage === 'totp' && (
          <div data-testid="admin-setup-totp">
            <p className="text-[14px] text-[#5C5852] mb-4">Scanne den QR-Code mit Google Authenticator, Authy oder einer anderen TOTP-App.</p>
            {qr && <img src={qr} alt="2FA QR-Code" data-testid="admin-setup-qr" className="mx-auto w-44 h-44 rounded-lg border border-[#E5E0D2]" />}
            <details className="mt-2 mb-4 text-[12px] text-[#5C5852]">
              <summary className="cursor-pointer underline">Schlüssel manuell eingeben</summary>
              <code data-testid="admin-setup-secret" className="block mt-2 p-2 bg-[#FAF8F2] rounded font-mono text-[12px] break-all">{secret}</code>
            </details>
            <label className="block mb-4">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">6-stelliger Code aus deiner App</span>
              <input type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} autoComplete="one-time-code"
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="admin-setup-otp" placeholder="123456"
                className="w-full px-3 py-3 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[24px] font-mono text-center tabular-nums tracking-[0.3em]"
              />
            </label>
            {error && <p data-testid="admin-setup-err" className="mb-3 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}
            <button type="button" onClick={completeSetup} disabled={busy || otp.length !== 6} data-testid="admin-setup-complete"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-40 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Einrichtung abschließen
            </button>
          </div>
        )}

        {stage === 'done' && (
          <div data-testid="admin-setup-done">
            <div className="flex items-center gap-2 text-[#7FC242] mb-4">
              <Check className="w-5 h-5" />
              <p className="text-[14.5px] font-semibold text-[#1F1D1A]">Eingerichtet!</p>
            </div>
            <p className="text-[13px] text-[#5C5852] mb-4">
              <strong className="text-[#1F1D1A]">Wichtig — Recovery-Codes:</strong> Falls du dein Smartphone verlierst, kannst du dich mit einem dieser Codes anmelden. Jeder Code funktioniert <em>nur einmal</em>. Speichere sie an einem sicheren Ort (Passwort-Manager, Ausdruck). Diese Codes werden nur jetzt einmalig angezeigt.
            </p>
            <ul data-testid="admin-setup-codes" className="grid grid-cols-2 gap-2 p-3 bg-[#FAF8F2] rounded-lg border border-[#E5E0D2] mb-4">
              {recoveryCodes.map((c) => (
                <li key={c} className="font-mono text-[13.5px] text-[#1F1D1A] tabular-nums">{c}</li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <button type="button" onClick={copyCodes} data-testid="admin-setup-copy-codes"
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-semibold transition-colors ${
                  copied ? 'bg-[#7FC242] text-white' : 'bg-white border border-[#E5E0D2] text-[#1F1D1A] hover:border-[#F26B1A]'
                }`}>
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Kopiert' : 'Codes kopieren'}
              </button>
              <button type="button" onClick={goToAdmin} data-testid="admin-setup-go-to-admin"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F26B1A] text-white text-[12.5px] font-semibold hover:bg-[#D85A0A] transition-colors">
                Zum Admin-Bereich <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
